import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '~environments/environment';
import {Address} from '../../models';
import {CustomerAddressTranslationsService, SalutationsTranslationService} from 'src/app/shared/translations';

/**
 * Service for creating and updating address entity.
 */
@Injectable()
export class AddressService {
  public API_URL_ADDRESSES: any = `${environment.envAPI}/addresses`;
  public API_URL_CUSTOMERS: any = `${environment.envAPI}/customers`;

  public addressList: BehaviorSubject<Array<Address>> = new BehaviorSubject<Array<Address>>(null);
  public castAddressList: any = this.addressList.asObservable();

  public currentAddresses: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castCurrentAddresses: any = this.currentAddresses.asObservable();

  public updateAddress: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castUpdateAddress: any = this.updateAddress.asObservable();

  constructor(
    private http: HttpClient,
    private customerAddressTranslations: CustomerAddressTranslationsService,
    private salutationsTranslations: SalutationsTranslationService
  ) {
  }

  /**
   * Create a new address.
   */
  public create(address: Address): any {
    return this.http.post(this.API_URL_ADDRESSES, address).pipe(
      map((response: any) => new Address(response, this.customerAddressTranslations, this.salutationsTranslations)));
  }

  /**
   * Update an address.
   */
  public update(address: Address): any {
    return this.http.patch(`${this.API_URL_ADDRESSES}/${address.id}`, address).pipe(
      map((response: any) => new Address(response, this.customerAddressTranslations, this.salutationsTranslations)));
  }

  /**
   * Delete an address.
   */
  public delete(addressId: number): any {
    return this.http.delete(`${this.API_URL_ADDRESSES}/${addressId}`);
  }

  /**
   * Get all addresses from a customer.
   */
  public getCustomerAll(customerId: number): any {
    return this.http.get(`${this.API_URL_CUSTOMERS}/${customerId}/addresses`);
  }

  /**
   * Adds values to the observer.
   */
  public announceCurrentAddresses(addresses: Array<Address>): void {
    this.currentAddresses.next(addresses);
  }

  /**
   * Adds values to the observer.
   */
  public announceUpdatedAddress(address: Address): void {
    this.updateAddress.next(address);
  }

  /**
   * Clear the address values from the observable.
   */
  public clearAddressUpdate(): void {
    this.updateAddress.next(null);
  }

  /**
   * Set a new address data in the address list
   */
  public setAddressList(addressList: Array<Address>): void {
    this.addressList.next(addressList);
    this.sortAddressesByType(this.addressList.getValue());
  }

  /**
   * Delete a certain address from the address list using the id
   */
  public deleteAddressFromList(addressId: number): void {
    const index = this.addressList.getValue().findIndex(el => el.id === addressId);
    this.addressList.getValue().splice(index, 1);
    this.sortAddressesByType(this.addressList.getValue());
  }

  /**
   * Add a new entry to the address list
   */
  public addToAddressList(address: Address): void {
    this.addressList.getValue().push(address);
    this.sortAddressesByType(this.addressList.getValue());
  }

  /**
   * Update a certain entry inside the address list
   */
  public updateAddressData(address: Address): void {
    const index = this.addressList.getValue().findIndex(el => el.id === address.id);
    this.addressList.getValue()[index] = address;
    this.sortAddressesByType(this.addressList.getValue());
  }

  /**
   * Sorts standard addresses before others
   */
  public sortAddressesByType(address: Array<Address>): void {
    address.sort((x, y) => (x.type === y.type) ? 0 : x.type === 'standard' ? -1 : 0);
  }
}
